import { Divider, Flex, Form, Input, Space, Typography, message } from "antd";
import Button from "../Atoms/DynamicButton";
import AuthService from "../../Services/auth.service";
import { openNotification } from "../Notification/Index";
import googleAnalyticService from "../../Services/google.analytic.service";
import { useAppContext } from "../../Context/AppContext/AppContext";
import { useLoader } from "../../Context/LoaderContext/LoaderContext";

interface SignUpFormProps {
  onSuccess?: () => void;
  onSignInClick?: () => void;
  showSignInLink?: boolean;
}

const SignUpForm: React.FC<SignUpFormProps> = ({
  onSuccess,
  onSignInClick,
  showSignInLink = true,
}) => {
  const { setLoading, setLoadingMessage, isLoading } = useLoader();
  const authService = AuthService.getInstance();
  const { organization } = useAppContext();

  const handleGoogleAuthClick = async () => {
    try {
      const domain = window.location.hostname;
      await authService.handleGoogleAuth(domain);
      googleAnalyticService.handleSubmissionEvents({
        category: "QualifyMe Candidate Signup",
        action: "Sign Up using Google",
        label: `Candidate user signed up successfully to organization ${
          organization?.orgName || "QualifyMe"
        }`,
      });
    } catch (error) {
      console.error("Unable to sign up with Google:", error);
      message.error("Unable to create your account. Please try again.");
    }
  };

  const handleLinkedinAuthClick = async () => {
    try {
      const domain = window.location.hostname;
      await authService.handleLinkedinAuth(domain);
    } catch (error) {
      console.error("Unable to sign up with LinkedIn:", error);
      message.error("Unable to create your account. Please try again.");
    }
  };

  const handleSignUp = async (formValues: any) => {
    try {
      setLoading(true);
      setLoadingMessage("Signing up...");
      const data = { ...formValues };
      delete data.confirm;
      const response = await authService.signUpUser(data);
      if (response) {
        if (response.status === 400) {
          openNotification(
            "error",
            "User already exists",
            "Please sign in to continue",
            5
          );
          return;
        }

        googleAnalyticService.handleSubmissionEvents({
          category: "QualifyMe Candidate Signup",
          action: "Sign Up using email",
          label: `Candidate user with email ${
            data.email
          } signed up successfully to organization ${
            organization?.orgName || "QualifyMe"
          }`,
        });

        const signInResponse = await authService.signIn({
          email: data.email,
          password: formValues.password,
        });

        if (signInResponse) {
          if (
            signInResponse.status === 500 ||
            signInResponse.status === 401 ||
            signInResponse.status === 400
          ) {
            openNotification(
              "error",
              "Sign in failed after signup",
              "Please try signing in manually"
            );
            return;
          }
          openNotification("success", "Account created successfully", "");
          onSuccess?.();
        }
      }
    } catch (err) {
      console.error("Unable to create your account:", err);
      message.error("Unable to create your account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      name="signup_form"
      className="auth-form"
      initialValues={{ remember: true }}
      onFinish={handleSignUp}
      autoComplete="off"
    >
      <Form.Item
        name="firstName"
        rules={[
          {
            required: true,
            message: "Please enter your first name!",
          },
          {
            pattern: /^[A-Za-z]{2,25}(\s[A-Za-z]{2,25})?$/,
            message:
              "Name must contain only letters. Middle name is optional!",
          },
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve();

              const totalLength = value.length;
              if (totalLength < 2 || totalLength > 51) {
                return Promise.reject(
                  new Error("Name must be 2-51 characters long!")
                );
              }

              const parts = value.trim().split(/\s+/);
              if (parts.length > 2) {
                return Promise.reject(
                  new Error("Only first name and middle name are allowed!")
                );
              }

              for (const part of parts) {
                if (part.length < 2 || part.length > 25) {
                  return Promise.reject(
                    new Error("Each name part must be 2-25 characters long!")
                  );
                }
              }

              return Promise.resolve();
            },
          },
        ]}
      >
        <Input placeholder="Firstname" />
      </Form.Item>
      <Form.Item
        name="lastName"
        // rules={[
        //   {
        //     required: true,
        //     message: "Please enter your last name!",
        //   },
        //   {
        //     pattern: /^[A-Za-z]{2,50}$/,
        //     message:
        //       "Last name must contain only letters and be 2-50 characters long!",
        //   },
        // ]}
      >
        <Input placeholder="Lastname" />
      </Form.Item>
      <Form.Item
        name="email"
        rules={[
          {
            type: "email",
            message: "The input is not valid E-mail!",
          },
          {
            required: true,
            message: "Please enter your email!",
          },
          {
            pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            message: "Please enter a valid email address!",
          },
        ]}
      >
        <Input placeholder="Email" />
      </Form.Item>

      <Form.Item
        name="password"
        rules={[
          {
            required: true,
            message: "Please enter your password!",
          },
          {
            min: 8,
            message: "Password must be at least 8 characters long!",
          },
          {
            pattern: /[A-Z]/,
            message: "Password must contain at least one uppercase letter!",
          },
          {
            pattern: /[a-z]/,
            message: "Password must contain at least one lowercase letter!",
          },
          {
            pattern: /[0-9]/,
            message: "Password must contain at least one number!",
          },
          {
            pattern: /[!@#$%^&*(),.?":{}|<>]/,
            message: "Password must contain at least one special character!",
          },
        ]}
      >
        <Input.Password placeholder="Password" />
      </Form.Item>
      <Form.Item
        name="confirm"
        dependencies={["password"]}
        rules={[
          {
            required: true,
            message: "Please confirm your password!",
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("password") === value) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error("The new password that you entered do not match!")
              );
            },
          }),
        ]}
      >
        <Input.Password placeholder="Confirm Password" />
      </Form.Item>
      <Space size={16} direction="vertical" style={{ width: "100%" }}>
        <Button type="primary" htmlType="submit" block loading={isLoading}>
          {isLoading ? "" : "Signup"}
        </Button>
        <Divider orientation="center">or</Divider>
        <Flex gap={10} vertical={true}>
          <Button
            icon={
              <img src="/icons/google.svg" alt="Google" style={{ width: 20 }} />
            }
            onClick={handleGoogleAuthClick}
            block
          >
            Sign up with Google
          </Button>
          <Button
            icon={
              <img
                src="/icons/linkedin.svg"
                alt="LinkedIn"
                style={{ width: 20 }}
              />
            }
            onClick={handleLinkedinAuthClick}
            block
          >
            Sign up with LinkedIn
          </Button>
        </Flex>
        {showSignInLink && (
          <Typography.Paragraph style={{ textAlign: "center", margin: 0 }}>
            Already have an account?
            <Button onClick={onSignInClick} type="link" loading={isLoading}>
              Sign In
            </Button>
          </Typography.Paragraph>
        )}
      </Space>
    </Form>
  );
};

export default SignUpForm;
